"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

function EmailVerified() {
  const navigate = useNavigate();

  const handleLogin = () => navigate("/login");
  const handleReturnToStore = () => navigate("/");

  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: { scaleX: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { scaleX: 0, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  const AnimatedLink = ({ children, onClick, ariaLabel }) => (
    <motion.button
      onClick={onClick}
      className="relative text-base cursor-pointer text-yellow-500 bg-transparent border-none p-0 hover:opacity-80 outline-none"
      aria-label={ariaLabel}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <motion.div
        className="flex flex-col items-center gap-10 p-10 bg-white rounded-3xl shadow-lg max-w-md w-full"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Success Icon */}
        <motion.div
          className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-yellow-100"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
        >
          <motion.svg
            className="h-10 w-10 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </motion.svg>
        </motion.div>

        {/* Header */}
        <motion.header
          className="text-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h1 className="text-4xl font-bold text-yellow-500 mb-2">Email Verified!</h1>
          <p className="text-gray-600 text-base leading-relaxed">
            Thank you for verifying your email address. Your account is now active and ready to use.
          </p>
        </motion.header>

        {/* Success Message */}
        <motion.div
          className="text-center bg-yellow-50 p-6 rounded-2xl border border-yellow-200 w-full"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p className="text-gray-700 text-sm">
            You can now access all features of your account. Welcome aboard!
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="flex flex-col gap-4 w-full"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <button
            onClick={handleLogin}
            className="w-full px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all transform hover:scale-105 active:scale-95"
            aria-label="Go to login page"
          >
            Continue to Login
          </button>

          <div className="flex justify-center pt-2">
            <AnimatedLink onClick={handleReturnToStore} ariaLabel="Return to store">
              Return to Store
            </AnimatedLink>
          </div>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          className="text-center text-xs text-gray-500 border-t border-gray-200 pt-6 w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p>
            If you have any questions, please contact our support team.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default EmailVerified;